/**
 * Fetch-based HTTP client — drop-in replacement for axios.
 * Supports: method, url, data (body), params (query), headers, validateStatus, onUploadProgress.
 */

import { v4 as uuidv4 } from 'uuid';

function buildUrl(url, params) {
	if (!params || Object.keys(params).length === 0) return url;
	const query = Object.entries(params)
		.filter(([, v]) => v !== undefined && v !== null)
		.map(([k, v]) => `${encodeURIComponent(k)}=${encodeValue(v)}`)
		.join('&');
	if (!query) return url;
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}${query}`;
}

function encodeValue(value) {
	return encodeURIComponent(value)
		.replace(/%20/g, '+')
		.replace(/%2C/gi, ',');
}

function isJsonContentType(headers) {
	const ct = Object.entries(headers).find(
		([k]) => k.toLowerCase() === 'content-type'
	);
	if (!ct) return true;
	return ct[1] && ct[1].includes('application/json');
}

async function httpClient(config) {
	const {
		method = 'GET',
		url,
		data,
		params,
		headers = {},
		validateStatus = (status) => status >= 200 && status < 300,
		onUploadProgress,
	} = typeof config === 'string' ? { url: config } : config;

	const fullUrl = buildUrl(url, params);
	const upperMethod = method.toUpperCase();

	const traceId = uuidv4();

	const fetchOptions = {
		method: upperMethod,
		headers: { ...headers, 'X-Trace-Id': traceId },
	};

	if (process.env.NODE_ENV === 'development') {
		console.log(`[Request] ${upperMethod} ${fullUrl} | X-Trace-Id: ${traceId}`);
	}

	if (data !== undefined && data !== null && upperMethod !== 'GET' && upperMethod !== 'HEAD') {
		if (data instanceof FormData || data instanceof Blob || data instanceof ArrayBuffer || typeof data === 'string') {
			fetchOptions.body = data;
		} else if (isJsonContentType(fetchOptions.headers)) {
			fetchOptions.body = JSON.stringify(data);
			if (!Object.keys(fetchOptions.headers).find(k => k.toLowerCase() === 'content-type')) {
				fetchOptions.headers['Content-Type'] = 'application/json';
			}
		} else {
			fetchOptions.body = data;
		}
	}

	let response;

	if (onUploadProgress && typeof XMLHttpRequest !== 'undefined') {
		response = await xhrUpload(fullUrl, fetchOptions, onUploadProgress);
	} else {
		response = await fetch(fullUrl, fetchOptions);
	}

	const responseData = await parseResponseBody(response);

	const result = {
		data: responseData,
		status: response.status,
		statusText: response.statusText,
		headers: Object.fromEntries(response.headers.entries()),
		config,
	};

	if (!validateStatus(response.status)) {
		const error = new Error(`Request failed with status ${response.status}`);
		error.response = result;
		error.config = config;
		throw error;
	}

	return result;
}

async function parseResponseBody(response) {
	const contentType = response.headers.get('content-type') || '';
	const text = await response.text();
	if (contentType.includes('application/json') || isLikelyJson(text)) {
		try {
			return JSON.parse(text);
		} catch {
			return text;
		}
	}
	return text;
}

function isLikelyJson(text) {
	const trimmed = text.trimStart();
	return trimmed.startsWith('{') || trimmed.startsWith('[');
}

function xhrUpload(url, options, onUploadProgress) {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open(options.method, url);

		Object.entries(options.headers || {}).forEach(([key, value]) => {
			xhr.setRequestHeader(key, value);
		});

		xhr.upload.addEventListener('progress', (event) => {
			if (event.lengthComputable) {
				onUploadProgress({ loaded: event.loaded, total: event.total });
			}
		});

		xhr.onload = () => {
			const headers = new Headers();
			xhr.getAllResponseHeaders().trim().split('\r\n').forEach(line => {
				const [key, ...rest] = line.split(': ');
				if (key) headers.set(key, rest.join(': '));
			});

			resolve({
				status: xhr.status,
				statusText: xhr.statusText,
				headers,
				text: () => Promise.resolve(xhr.responseText),
			});
		};

		xhr.onerror = () => reject(new Error('Network error'));
		xhr.send(options.body);
	});
}

httpClient.get = (url, config = {}) => httpClient({ ...config, method: 'GET', url });
httpClient.post = (url, data, config = {}) => httpClient({ ...config, method: 'POST', url, data });
httpClient.put = (url, data, config = {}) => httpClient({ ...config, method: 'PUT', url, data });
httpClient.delete = (url, config = {}) => httpClient({ ...config, method: 'DELETE', url });
httpClient.patch = (url, data, config = {}) => httpClient({ ...config, method: 'PATCH', url, data });

export default httpClient;
